// Supabase Staff Management Service - Production Ready
// Handles staff data in Supabase database with proper RLS and relationships

import { supabase } from './supabase';
import { logger } from '../utils/logger';

export const staffService = {
  /**
   * Get all staff members for a specific physician
   * @param {string} physicianId - The healthcare provider's UUID
   * @returns {Promise<Array>} Array of staff members
   */
  async getStaffForPhysician(physicianId) {
    try {
      const { data: staff, error } = await supabase
        .from('staff_members')
        .select(`
          id,
          name,
          role,
          email,
          phone,
          is_active,
          dietary_restrictions,
          favorite_cuisines,
          allergens,
          notes,
          order_history,
          created_at,
          updated_at
        `)
        .eq('physician_id', physicianId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (error) {
        logger.error('Error loading staff from Supabase', { error: error.message });
        throw error;
      }

      return staff || [];
    } catch (error) {
      logger.error('Error getting staff', { error: error.message });
      return [];
    }
  },

  /**
   * Get all staff members across all physicians (for admin/reporting)
   * @returns {Promise<Array>} Array of all staff members with physician info
   */
  async getAllStaff() {
    try {
      const { data: staff, error } = await supabase
        .from('staff_members')
        .select(`
          *,
          physician:user_profiles!physician_id(
            id,
            first_name,
            last_name,
            email,
            company
          )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return staff || [];
    } catch (error) {
      logger.error('Error getting all staff', { error: error.message });
      return [];
    }
  },

  /**
   * Add a new staff member
   * @param {string} physicianId - The healthcare provider's UUID
   * @param {Object} staffData - Staff member data
   * @returns {Promise<Object|null>} Created staff member or null
   */
  async addStaffMember(physicianId, staffData) {
    try {
      logger.debug('➕ SupabaseStaffService: Adding staff member:', { physicianId, staffData });

      const { data: newStaff, error } = await supabase
        .from('staff_members')
        .insert([{
          physician_id: physicianId,
          name: staffData.name,
          role: staffData.role,
          email: staffData.email || null,
          phone: staffData.phone || null,
          department: staffData.department || 'Clinical',
          dietary_restrictions: staffData.dietaryRestrictions || [],
          favorite_cuisines: staffData.favoriteCuisines || [],
          allergens: staffData.allergens || [],
          notes: staffData.notes || '',
          is_active: true
        }])
        .select()
        .single();

      if (error) {
        logger.error('Error adding staff member', { error: error.message });
        throw error;
      }

      logger.debug('✅ SupabaseStaffService: Successfully added staff member:', newStaff);
      return newStaff;
    } catch (error) {
      logger.error('SupabaseStaffService: Error adding staff member', { error: error.message });
      return null;
    }
  },

  /**
   * Update an existing staff member
   * @param {string} staffId - Staff member UUID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object|null>} Updated staff member or null
   */
  async updateStaffMember(staffId, updates) {
    try {
      logger.debug('🔄 SupabaseStaffService: Updating staff member:', { staffId, updates });

      const updateData = {};

      // Map common fields
      if (updates.name) updateData.name = updates.name;
      if (updates.role) updateData.role = updates.role;
      if (updates.email !== undefined) updateData.email = updates.email;
      if (updates.phone !== undefined) updateData.phone = updates.phone;
      if (updates.department !== undefined) updateData.department = updates.department;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if (updates.dietaryRestrictions) updateData.dietary_restrictions = updates.dietaryRestrictions;
      if (updates.favoriteCuisines) updateData.favorite_cuisines = updates.favoriteCuisines;
      if (updates.allergens) updateData.allergens = updates.allergens;
      if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

      const { data: updatedStaff, error } = await supabase
        .from('staff_members')
        .update(updateData)
        .eq('id', staffId)
        .select()
        .single();

      if (error) {
        logger.error('Error updating staff member', { error: error.message });
        throw error;
      }

      logger.debug('✅ SupabaseStaffService: Successfully updated staff member:', updatedStaff);
      return updatedStaff;
    } catch (error) {
      logger.error('SupabaseStaffService: Error updating staff member', { error: error.message });
      return null;
    }
  },

  /**
   * Deactivate a staff member (soft delete)
   * @param {string} staffId - Staff member UUID
   * @returns {Promise<boolean>} Success status
   */
  async deactivateStaffMember(staffId) {
    try {
      logger.debug('🗑️ SupabaseStaffService: Deactivating staff member:', staffId);

      const { error } = await supabase
        .from('staff_members')
        .update({
          is_active: false,
          deactivated_at: new Date().toISOString()
        })
        .eq('id', staffId);

      if (error) {
        logger.error('Error deactivating staff member', { error: error.message });
        throw error;
      }

      logger.debug('✅ SupabaseStaffService: Successfully deactivated staff member');
      return true;
    } catch (error) {
      logger.error('SupabaseStaffService: Error deactivating staff member', { error: error.message });
      return false;
    }
  },

  /**
   * Record staff orders for a meeting
   * @param {string} meetingId - Meeting UUID
   * @param {Array} staffOrders - Array of staff order objects
   * @returns {Promise<boolean>} Success status
   */
  async recordStaffOrders(meetingId, staffOrders) {
    try {
      logger.debug('📝 SupabaseStaffService: Recording staff orders:', { meetingId, staffOrders });

      // Update each staff member's order history
      const updatePromises = staffOrders.map(async (order) => {
        if (!order.staffId) return null;

        // Get current staff member
        const { data: staffMember, error: fetchError } = await supabase
          .from('staff_members')
          .select('order_history')
          .eq('id', order.staffId)
          .single();

        if (fetchError) {
          logger.error('Error fetching staff member for order update', { error: fetchError.message });
          return null;
        }

        // Add new order to history
        const currentHistory = staffMember.order_history || [];
        const newOrder = {
          meeting_id: meetingId,
          items: order.items || [],
          individual_cost: order.individualCost || 0,
          special_requests: order.specialRequests || '',
          attended: order.attended !== false,
          order_timestamp: new Date().toISOString()
        };

        const updatedHistory = [...currentHistory, newOrder];

        // Keep only last 50 orders per staff member
        if (updatedHistory.length > 50) {
          updatedHistory.splice(0, updatedHistory.length - 50);
        }

        // Update staff member with new order history
        const { error: updateError } = await supabase
          .from('staff_members')
          .update({ order_history: updatedHistory })
          .eq('id', order.staffId);

        if (updateError) {
          logger.error('Error updating staff order history', { error: updateError.message });
          return null;
        }

        return true;
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r === true).length;

      logger.debug(`✅ SupabaseStaffService: Updated order history for ${successCount}/${staffOrders.length} staff members`);
      return successCount === staffOrders.length;
    } catch (error) {
      logger.error('SupabaseStaffService: Error recording staff orders', { error: error.message });
      return false;
    }
  },

  /**
   * Get staff analytics for a physician
   * @param {string} physicianId - Physician UUID
   * @param {number} daysBack - Number of days to analyze
   * @returns {Promise<Object>} Analytics data
   */
  async getStaffAnalytics(physicianId, daysBack = 30) {
    try {
      logger.debug('📊 SupabaseStaffService: Getting staff analytics:', { physicianId, daysBack });

      const { data: staff, error } = await supabase
        .from('staff_members')
        .select('*')
        .eq('physician_id', physicianId)
        .eq('is_active', true);

      if (error) throw error;

      const analytics = {
        totalStaff: staff.length,
        totalMeetings: 0,
        avgStaffPerMeeting: 0,
        totalStaffCost: 0,
        mostActiveStaff: [],
        dietaryRestrictionsSummary: {},
        popularCuisines: {}
      };

      // Analyze order history for metrics
      staff.forEach(staffMember => {
        const orderHistory = staffMember.order_history || [];
        const recentOrders = orderHistory.filter(order => {
          const orderDate = new Date(order.order_timestamp);
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - daysBack);
          return orderDate >= cutoffDate;
        });

        analytics.totalMeetings += recentOrders.length;
        analytics.totalStaffCost += recentOrders.reduce((sum, order) => sum + (order.individual_cost || 0), 0);

        // Count dietary restrictions
        staffMember.dietary_restrictions?.forEach(restriction => {
          analytics.dietaryRestrictionsSummary[restriction] =
            (analytics.dietaryRestrictionsSummary[restriction] || 0) + 1;
        });

        // Count favorite cuisines
        staffMember.favorite_cuisines?.forEach(cuisine => {
          analytics.popularCuisines[cuisine] =
            (analytics.popularCuisines[cuisine] || 0) + 1;
        });
      });

      if (analytics.totalMeetings > 0) {
        analytics.avgStaffPerMeeting = staff.length; // Approximate
      }

      logger.debug('✅ SupabaseStaffService: Generated analytics:', analytics);
      return analytics;
    } catch (error) {
      logger.error('SupabaseStaffService: Error getting analytics', { error: error.message });
      return {
        totalStaff: 0,
        totalMeetings: 0,
        avgStaffPerMeeting: 0,
        totalStaffCost: 0,
        mostActiveStaff: [],
        dietaryRestrictionsSummary: {},
        popularCuisines: {}
      };
    }
  },

  /**
   * Migrate localStorage staff data to Supabase
   * @param {string} physicianId - Physician UUID
   * @param {Array} localStaffData - Staff data from localStorage
   * @returns {Promise<boolean>} Migration success status
   */
  async migrateLocalStaffData(physicianId, localStaffData) {
    try {
      logger.debug('🔄 SupabaseStaffService: Migrating local staff data:', { physicianId, count: localStaffData.length });

      const migrationPromises = localStaffData.map(async (localStaff) => {
        try {
          const migrationData = {
            name: localStaff.name,
            role: localStaff.role,
            email: localStaff.email,
            phone: localStaff.phone,
            dietaryRestrictions: localStaff.preferences?.dietaryRestrictions || localStaff.dietaryRestrictions || [],
            favoriteCuisines: localStaff.preferences?.favoriteCuisines || localStaff.favoriteCuisines || [],
            allergens: localStaff.preferences?.allergens || localStaff.allergens || [],
            notes: localStaff.preferences?.notes || localStaff.notes || ''
          };

          return await this.addStaffMember(physicianId, migrationData);
        } catch (error) {
          logger.error('Error migrating individual staff member', { staffName: localStaff.name, error: error.message });
          return null;
        }
      });

      const results = await Promise.all(migrationPromises);
      const successCount = results.filter(r => r !== null).length;

      logger.debug(`✅ SupabaseStaffService: Successfully migrated ${successCount}/${localStaffData.length} staff members`);
      return successCount === localStaffData.length;
    } catch (error) {
      logger.error('SupabaseStaffService: Error migrating staff data', { error: error.message });
      return false;
    }
  },

  /**
   * Get primary admin/office manager for a physician
   * First checks admin_users table for designated admins
   * Falls back to staff_members table if no admin relationship exists
   * @param {string} physicianId - The healthcare provider's UUID
   * @returns {Promise<Object|null>} Primary admin staff member or null
   */
  async getPrimaryAdmin(physicianId) {
    try {
      logger.debug('👔 SupabaseStaffService: Getting primary admin for physician:', physicianId);

      // First, check the admin_users table for designated admins
      const { data: adminUsers, error: adminError } = await supabase
        .from('admin_users')
        .select(`
          id,
          role,
          permissions,
          staff:user_profiles!admin_users_staff_id_fkey (
            id,
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq('customer_id', physicianId)
        .order('created_at', { ascending: true });

      if (!adminError && adminUsers && adminUsers.length > 0) {
        // Found admin user(s) - return the first one
        const adminUser = adminUsers[0];
        const staffProfile = adminUser.staff;

        if (staffProfile) {
          const admin = {
            id: staffProfile.id,
            name: `${staffProfile.first_name || ''} ${staffProfile.last_name || ''}`.trim(),
            role: adminUser.role || 'Office Manager',
            email: staffProfile.email,
            phone: staffProfile.phone
          };
          logger.debug('✅ Found admin from admin_users table:', admin);
          return admin;
        }
      }

      logger.debug('No admin found in admin_users table, checking staff_members...');

      // Fallback: Check staff_members table for admin-type roles
      const adminRoles = [
        'office_manager',
        'Office Manager',
        'practice_coordinator',
        'Practice Coordinator',
        'lead_medical_assistant',
        'Lead Medical Assistant',
        'scheduler',
        'Scheduler',
        'admin',
        'Admin',
        'Administrator'
      ];

      const { data: staff, error: staffError } = await supabase
        .from('staff_members')
        .select('id, name, role, email, phone')
        .eq('physician_id', physicianId)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (staffError) {
        logger.error('Error loading staff from Supabase', { error: staffError.message });
        throw staffError;
      }

      if (!staff || staff.length === 0) {
        logger.debug('No staff found for physician');
        return null;
      }

      // Try to find an admin by role
      for (const adminRole of adminRoles) {
        const admin = staff.find(s => s.role?.toLowerCase().includes(adminRole.toLowerCase()));
        if (admin) {
          logger.debug('✅ Found admin by role in staff_members:', admin);
          return admin;
        }
      }

      // If no admin role found, return the first staff member with contact info
      const staffWithContact = staff.find(s => s.email || s.phone);
      if (staffWithContact) {
        logger.debug('✅ Using first staff member with contact info as admin:', staffWithContact);
        return staffWithContact;
      }

      // Last resort: return first staff member
      logger.debug('✅ Using first staff member as admin:', staff[0]);
      return staff[0];
    } catch (error) {
      logger.error('SupabaseStaffService: Error getting primary admin', { error: error.message });
      return null;
    }
  },

  /**
   * Get all admin users (office staff) who manage a specific physician
   * @param {string} physicianId - The healthcare provider's UUID
   * @returns {Promise<Array>} Array of admin users with their profile info
   */
  async getAdminUsersForPhysician(physicianId) {
    try {
      const { data: adminRelationships, error } = await supabase
        .from('admin_users')
        .select(`
          id,
          staff_id,
          role,
          status,
          invited_email,
          created_at,
          updated_at,
          staff:user_profiles!staff_id(
            id,
            first_name,
            last_name,
            email,
            phone,
            user_type,
            title
          )
        `)
        .eq('customer_id', physicianId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error loading admin users from Supabase', { error: error.message });
        throw error;
      }

      // Transform the data to a flatter structure
      const admins = (adminRelationships || []).map(rel => {
        // Handle pending invitations (no staff_id yet)
        if (!rel.staff_id && rel.status === 'pending') {
          return {
            id: rel.id,
            staffId: null,
            role: rel.role,
            title: rel.role || 'Office Staff', // Add title for display
            name: rel.invited_email, // Use email as name for pending
            firstName: null,
            lastName: null,
            email: rel.invited_email,
            phone: null,
            userType: 'office_staff',
            status: 'pending',
            inviteStatus: 'pending', // For TeamManagement component
            isActive: false,
            invitedAt: rel.created_at,
            createdAt: rel.created_at,
            updatedAt: rel.updated_at,
            permissions: rel.permissions || []
          };
        }

        // Handle active admin users
        const activeStatus = rel.status || 'active';
        return {
          id: rel.id,
          staffId: rel.staff_id,
          role: rel.role,
          title: rel.staff?.title || rel.role || 'Office Manager', // Use user_profiles.title first, fallback to role
          name: rel.staff ? `${rel.staff.first_name || ''} ${rel.staff.last_name || ''}`.trim() : 'Unknown',
          firstName: rel.staff?.first_name,
          lastName: rel.staff?.last_name,
          email: rel.staff?.email || rel.invited_email,
          phone: rel.staff?.phone,
          userType: rel.staff?.user_type || 'office_staff',
          status: activeStatus,
          inviteStatus: activeStatus === 'active' ? 'accepted' : activeStatus, // Map active to accepted for display
          isActive: activeStatus === 'active',
          invitedAt: rel.created_at,
          createdAt: rel.created_at,
          updatedAt: rel.updated_at,
          permissions: rel.permissions || []
        };
      });

      return admins;
    } catch (error) {
      logger.error('Error getting admin users', { error: error.message });
      return [];
    }
  },

  /**
   * Remove an admin user relationship
   * @param {string} adminRelationshipId - The admin_users table ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteAdminUser(adminRelationshipId) {
    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', adminRelationshipId);

      if (error) {
        logger.error('Error deleting admin user', { error: error.message });
        throw error;
      }

      return true;
    } catch (error) {
      logger.error('Error deleting admin user', { error: error.message });
      return false;
    }
  },

  /**
   * Create a pending admin invitation (before office staff signs up)
   * @param {string} physicianId - The healthcare provider's UUID
   * @param {Object} inviteData - Invitation details (email, role, name)
   * @returns {Promise<Object|null>} Created invitation record
   */
  async createPendingAdminInvitation(physicianId, inviteData) {
    try {
      logger.debug('Creating pending admin invitation', { physicianId, email: inviteData.email });

      const { data: invitation, error } = await supabase
        .from('admin_users')
        .insert([{
          customer_id: physicianId,
          staff_id: null, // Will be filled when office staff signs up
          invited_email: inviteData.email.toLowerCase().trim(),
          role: inviteData.role || 'office_manager',
          status: 'pending'
        }])
        .select()
        .single();

      if (error) {
        logger.error('Error creating pending admin invitation', { error: error.message });
        throw error;
      }

      logger.debug('✅ Created pending admin invitation', { invitationId: invitation.id });
      return invitation;
    } catch (error) {
      logger.error('Error creating pending admin invitation', { error: error.message });
      return null;
    }
  },

  /**
   * Check if there's a pending invitation for an email address
   * @param {string} email - Email address to check
   * @returns {Promise<Object|null>} Pending invitation if found
   */
  async getPendingInvitationByEmail(email) {
    try {
      const { data: invitation, error } = await supabase
        .from('admin_users')
        .select(`
          id,
          customer_id,
          role,
          invited_email,
          created_at,
          physician:user_profiles!customer_id(
            id,
            first_name,
            last_name,
            email,
            company,
            specialty
          )
        `)
        .eq('invited_email', email.toLowerCase().trim())
        .eq('status', 'pending')
        .is('staff_id', null)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        logger.error('Error checking for pending invitation', { error: error.message });
        throw error;
      }

      return invitation || null;
    } catch (error) {
      logger.error('Error getting pending invitation', { error: error.message });
      return null;
    }
  },

  /**
   * Activate a pending invitation by linking it to a staff user
   * @param {string} invitationId - The admin_users record ID
   * @param {string} staffId - The office staff user's UUID
   * @returns {Promise<boolean>} Success status
   */
  async activatePendingInvitation(invitationId, staffId) {
    try {
      logger.debug('Activating pending invitation', { invitationId, staffId });

      const { error } = await supabase
        .from('admin_users')
        .update({
          staff_id: staffId,
          status: 'active'
        })
        .eq('id', invitationId);

      if (error) {
        logger.error('Error activating invitation', { error: error.message });
        throw error;
      }

      logger.debug('✅ Activated pending invitation', { invitationId });
      return true;
    } catch (error) {
      logger.error('Error activating invitation', { error: error.message });
      return false;
    }
  }
};

export default staffService;